'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';

// ─── Types ───────────────────────────────────────────────────────────────────
interface CRMOverview {
  contacts: { total: number; new_this_month: number; vip: number; corporate: number };
  leads: { total: number; new_today: number; qualified: number; converted: number; conversion_rate: number };
  pipeline: { total_value: number; opportunities: number; avg_deal_size: number; win_rate: number };
  tickets: { open: number; in_progress: number; resolved_today: number; avg_response_hours: number };
  campaigns: { active: number; sent_this_month: number; avg_open_rate: number; revenue_attributed: number };
  revenue: { forecast_30d: number; forecast_90d: number; clv_avg: number; top_segments: string[] };
}

interface Lead {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  source: string;
  status: string;
  score: number;
  destination: string;
  budget: number;
  created_at: string;
}

interface Opportunity {
  id: string;
  title: string;
  stage: string;
  value: number;
  probability: number;
  expected_close_date: string;
  destination: string;
}

interface Ticket {
  id: string;
  ticket_number: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  channel: string;
  created_at: string;
}

interface Campaign {
  id: string;
  name: string;
  type: string;
  status: string;
  audience_count: number;
  sent_count: number;
  opened_count: number;
  clicked_count: number;
  revenue_attributed: number;
  created_at: string;
}

interface Segment {
  id: string;
  name: string;
  type: string;
  contact_count: number;
  tags: string[];
  is_active: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (n: number) => n?.toLocaleString() ?? '0';
const fmtCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

const STAGE_COLORS: Record<string, string> = {
  prospecting: 'bg-slate-500/20 text-slate-300',
  qualification: 'bg-blue-500/20 text-blue-300',
  proposal: 'bg-amber-500/20 text-amber-300',
  negotiation: 'bg-orange-500/20 text-orange-300',
  closed_won: 'bg-emerald-500/20 text-emerald-300',
  closed_lost: 'bg-red-500/20 text-red-300',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-slate-500/20 text-slate-300',
  medium: 'bg-blue-500/20 text-blue-300',
  high: 'bg-amber-500/20 text-amber-300',
  urgent: 'bg-red-500/20 text-red-300',
  critical: 'bg-red-700/30 text-red-200',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-500/20 text-blue-300',
  contacted: 'bg-purple-500/20 text-purple-300',
  qualified: 'bg-emerald-500/20 text-emerald-300',
  converted: 'bg-teal-500/20 text-teal-300',
  lost: 'bg-red-500/20 text-red-300',
  open: 'bg-amber-500/20 text-amber-300',
  in_progress: 'bg-blue-500/20 text-blue-300',
  resolved: 'bg-emerald-500/20 text-emerald-300',
  closed: 'bg-slate-500/20 text-slate-300',
  draft: 'bg-slate-500/20 text-slate-300',
  running: 'bg-emerald-500/20 text-emerald-300',
  completed: 'bg-teal-500/20 text-teal-300',
  scheduled: 'bg-blue-500/20 text-blue-300',
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'ChartBarIcon' },
  { id: 'leads', label: 'Leads & Pipeline', icon: 'FunnelIcon' },
  { id: 'campaigns', label: 'Campaigns', icon: 'MegaphoneIcon' },
  { id: 'tickets', label: 'Support & Tickets', icon: 'TicketIcon' },
  { id: 'segments', label: 'Segments & Loyalty', icon: 'UserGroupIcon' },
  { id: 'copilot', label: 'AI Copilot', icon: 'SparklesIcon' },
] as const;

type TabId = (typeof TABS)[number]['id'];

// ─── Mock data for demo ───────────────────────────────────────────────────────
const MOCK_LEADS: Lead[] = [
  { id: '1', first_name: 'Alexandra', last_name: 'Chen', email: 'alex.chen@apex.com', phone: '+1 555 0101', source: 'referral', status: 'qualified', score: 92, destination: 'Maldives', budget: 28000, created_at: '2026-08-03T10:00:00Z' },
  { id: '2', first_name: 'Marcus', last_name: 'Okafor', email: 'm.okafor@meridian.co', phone: '+44 20 7946 0102', source: 'partner', status: 'contacted', score: 78, destination: 'Safari Kenya', budget: 45000, created_at: '2026-08-03T11:30:00Z' },
  { id: '3', first_name: 'Isabelle', last_name: 'Fontaine', email: 'i.fontaine@gmail.com', phone: '+33 1 42 86 0103', source: 'website', status: 'new', score: 65, destination: 'Antarctica', budget: 32000, created_at: '2026-08-04T08:15:00Z' },
  { id: '4', first_name: 'Raj', last_name: 'Patel', email: 'raj.patel@summit.in', phone: '+91 98765 43210', source: 'email', status: 'qualified', score: 84, destination: 'Bhutan', budget: 18000, created_at: '2026-08-04T09:45:00Z' },
  { id: '5', first_name: 'Sofia', last_name: 'Andersen', email: 'sofia.a@nordic.no', phone: '+47 22 00 0105', source: 'social', status: 'new', score: 51, destination: 'Galapagos', budget: 22000, created_at: '2026-08-04T12:00:00Z' },
];

const MOCK_OPPS: Opportunity[] = [
  { id: '1', title: 'Apex Global — Maldives Corporate Retreat', stage: 'proposal', value: 284000, probability: 65, expected_close_date: '2026-08-20', destination: 'Maldives' },
  { id: '2', title: 'Meridian Agency — Kenya Safari Group', stage: 'negotiation', value: 156000, probability: 80, expected_close_date: '2026-08-15', destination: 'Kenya' },
  { id: '3', title: 'Chen Family — Antarctica Expedition', stage: 'qualification', value: 96000, probability: 40, expected_close_date: '2026-09-10', destination: 'Antarctica' },
  { id: '4', title: 'Summit Consulting — Bhutan Wellness', stage: 'prospecting', value: 72000, probability: 20, expected_close_date: '2026-09-30', destination: 'Bhutan' },
  { id: '5', title: 'Nordic B2B — Galapagos Discovery', stage: 'closed_won', value: 198000, probability: 100, expected_close_date: '2026-07-30', destination: 'Galapagos' },
];

const MOCK_TICKETS: Ticket[] = [
  { id: '1', ticket_number: 'TKT-A1B2C', subject: 'Flight change request — Maldives booking', category: 'booking', priority: 'high', status: 'in_progress', channel: 'email', created_at: '2026-08-04T07:00:00Z' },
  { id: '2', ticket_number: 'TKT-D3E4F', subject: 'Refund request — cancelled safari', category: 'refund', priority: 'urgent', status: 'open', channel: 'phone', created_at: '2026-08-04T08:30:00Z' },
  { id: '3', ticket_number: 'TKT-G5H6I', subject: 'Visa documentation assistance', category: 'inquiry', priority: 'medium', status: 'open', channel: 'whatsapp', created_at: '2026-08-04T09:15:00Z' },
  { id: '4', ticket_number: 'TKT-J7K8L', subject: 'VIP upgrade request — Crystal Cruise', category: 'vip', priority: 'high', status: 'resolved', channel: 'chat', created_at: '2026-08-03T14:00:00Z' },
  { id: '5', ticket_number: 'TKT-M9N0O', subject: 'Payment failed — package booking', category: 'payment', priority: 'urgent', status: 'in_progress', channel: 'portal', created_at: '2026-08-04T10:45:00Z' },
];

const MOCK_CAMPAIGNS: Campaign[] = [
  { id: '1', name: 'August Luxury Escapes', type: 'email', status: 'running', audience_count: 4200, sent_count: 4200, opened_count: 1260, clicked_count: 378, revenue_attributed: 84000, created_at: '2026-08-01T00:00:00Z' },
  { id: '2', name: 'Safari Season WhatsApp Blast', type: 'whatsapp', status: 'completed', audience_count: 1800, sent_count: 1800, opened_count: 1440, clicked_count: 576, revenue_attributed: 156000, created_at: '2026-07-28T00:00:00Z' },
  { id: '3', name: 'VIP Re-engagement SMS', type: 'sms', status: 'scheduled', audience_count: 320, sent_count: 0, opened_count: 0, clicked_count: 0, revenue_attributed: 0, created_at: '2026-08-04T00:00:00Z' },
  { id: '4', name: 'Honeymoon Packages Newsletter', type: 'email', status: 'draft', audience_count: 2100, sent_count: 0, opened_count: 0, clicked_count: 0, revenue_attributed: 0, created_at: '2026-08-04T00:00:00Z' },
];

const MOCK_SEGMENTS: Segment[] = [
  { id: '1', name: 'VIP Travelers', type: 'dynamic', contact_count: 142, tags: ['vip', 'high-value'], is_active: true },
  { id: '2', name: 'Luxury Cruise Enthusiasts', type: 'dynamic', contact_count: 387, tags: ['cruise', 'luxury'], is_active: true },
  { id: '3', name: 'Lapsed Customers', type: 'dynamic', contact_count: 1204, tags: ['re-engagement'], is_active: true },
  { id: '4', name: 'Corporate Accounts', type: 'static', contact_count: 89, tags: ['corporate', 'b2b'], is_active: true },
  { id: '5', name: 'Safari Seekers', type: 'dynamic', contact_count: 256, tags: ['safari', 'africa'], is_active: true },
  { id: '6', name: 'Honeymoon Couples', type: 'dynamic', contact_count: 178, tags: ['honeymoon', 'romance'], is_active: true },
];

const COPILOT_SUGGESTIONS = [
  { type: 'action', icon: 'BoltIcon', color: 'text-amber-400', title: 'Follow up with 3 hot leads', desc: 'Alexandra Chen, Marcus Okafor, and Raj Patel have scores above 75 and no contact in 48h.' },
  { type: 'insight', icon: 'LightBulbIcon', color: 'text-blue-400', title: 'Pipeline at risk', desc: '$284K Apex Global deal has been in Proposal stage for 12 days — probability dropping. Send revised quote.' },
  { type: 'opportunity', icon: 'SparklesIcon', color: 'text-emerald-400', title: 'Upsell opportunity', desc: '23 Gold tier customers booked safaris last year — target with Antarctica expedition campaign.' },
  { type: 'alert', icon: 'ExclamationTriangleIcon', color: 'text-red-400', title: 'SLA breach risk', desc: '2 urgent tickets (TKT-D3E4F, TKT-M9N0O) approaching 4-hour SLA threshold.' },
  { type: 'forecast', icon: 'ChartBarIcon', color: 'text-purple-400', title: 'Revenue forecast', desc: 'Q4 pipeline suggests $3.82M — 18% above target. Recommend increasing capacity for December departures.' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────
function KPICard({ label, value, sub, icon, color }: { label: string; value: string; sub?: string; icon: string; color: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface-2 p-4 flex items-start gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={20} />
      </div>
      <div className="min-w-0">
        <p className="text-2xs text-muted-foreground uppercase tracking-widest mb-0.5">{label}</p>
        <p className="text-xl font-bold text-foreground">{value}</p>
        {sub && <p className="text-2xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function Badge({ label, className }: { label: string; className?: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-semibold uppercase tracking-wide ${className}`}>
      {label}
    </span>
  );
}

function ScoreBar({ score }: { score: number }) {
  const color = score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-500' : score >= 40 ? 'bg-blue-500' : 'bg-slate-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-surface-3">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-2xs font-bold text-foreground w-6 text-right">{score}</span>
    </div>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────
function OverviewTab({ overview }: { overview: CRMOverview | null }) {
  if (!overview) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const { contacts, leads, pipeline, tickets, campaigns, revenue } = overview;

  const integrations = [
    { name: 'AI Concierge', status: 'live', icon: 'SparklesIcon' },
    { name: 'Booking Engine', status: 'live', icon: 'CalendarDaysIcon' },
    { name: 'Tour Platform', status: 'live', icon: 'GlobeAltIcon' },
    { name: 'Flights', status: 'live', icon: 'PaperAirplaneIcon' },
    { name: 'Hotels', status: 'live', icon: 'BuildingOffice2Icon' },
    { name: 'Dynamic Packages', status: 'live', icon: 'PuzzlePieceIcon' },
    { name: 'Supplier Registry', status: 'live', icon: 'BuildingStorefrontIcon' },
    { name: 'Enterprise Admin', status: 'live', icon: 'ChartBarIcon' },
    { name: 'Analytics', status: 'live', icon: 'PresentationChartLineIcon' },
    { name: 'Multi-Tenant', status: 'live', icon: 'BuildingOffice2Icon' },
    { name: 'G Adventures', status: 'live', icon: 'MapPinIcon' },
    { name: 'Viator', status: 'live', icon: 'TicketIcon' },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KPICard label="Total Contacts" value={fmt(contacts.total)} sub={`+${contacts.new_this_month} this month`} icon="UsersIcon" color="bg-blue-500/10 text-blue-400" />
        <KPICard label="Active Leads" value={fmt(leads.total)} sub={`${leads.new_today} new today`} icon="FunnelIcon" color="bg-amber-500/10 text-amber-400" />
        <KPICard label="Pipeline Value" value={fmtCurrency(pipeline.total_value)} sub={`${pipeline.win_rate}% win rate`} icon="BanknotesIcon" color="bg-emerald-500/10 text-emerald-400" />
        <KPICard label="Open Tickets" value={fmt(tickets.open)} sub={`${tickets.avg_response_hours}h avg response`} icon="TicketIcon" color="bg-red-500/10 text-red-400" />
        <KPICard label="Active Campaigns" value={fmt(campaigns.active)} sub={`${campaigns.avg_open_rate}% open rate`} icon="MegaphoneIcon" color="bg-purple-500/10 text-purple-400" />
        <KPICard label="30-Day Forecast" value={fmtCurrency(revenue.forecast_30d)} sub={`CLV avg ${fmtCurrency(revenue.clv_avg)}`} icon="ChartBarIcon" color="bg-teal-500/10 text-teal-400" />
      </div>

      {/* Pipeline Stages */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="FunnelIcon" size={16} className="text-primary" />
          Sales Pipeline Stages
        </h3>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {[
            { stage: 'Prospecting', count: 67, value: 1240000, color: 'border-slate-500' },
            { stage: 'Qualification', count: 45, value: 890000, color: 'border-blue-500' },
            { stage: 'Proposal', count: 34, value: 1580000, color: 'border-amber-500' },
            { stage: 'Negotiation', count: 18, value: 720000, color: 'border-orange-500' },
            { stage: 'Won', count: 187, value: 4280000, color: 'border-emerald-500' },
            { stage: 'Lost', count: 43, value: 0, color: 'border-red-500' },
          ].map(s => (
            <div key={s.stage} className={`rounded-lg border-l-2 ${s.color} bg-surface-3 p-3`}>
              <p className="text-2xs text-muted-foreground uppercase tracking-wide">{s.stage}</p>
              <p className="text-lg font-bold text-foreground mt-1">{s.count}</p>
              <p className="text-2xs text-muted-foreground">{fmtCurrency(s.value)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Revenue Forecast + Integrations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-surface-2 p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="ChartBarIcon" size={16} className="text-primary" />
            Revenue Forecast
          </h3>
          <div className="space-y-3">
            {[
              { label: '30-Day Forecast', value: revenue.forecast_30d, max: revenue.forecast_90d, color: 'bg-primary' },
              { label: '90-Day Forecast', value: revenue.forecast_90d, max: revenue.forecast_90d, color: 'bg-emerald-500' },
              { label: 'Avg Customer LTV', value: revenue.clv_avg, max: 50000, color: 'bg-amber-500' },
            ].map(r => (
              <div key={r.label}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{r.label}</span>
                  <span className="font-semibold text-foreground">{fmtCurrency(r.value)}</span>
                </div>
                <div className="h-2 rounded-full bg-surface-3">
                  <div className={`h-2 rounded-full ${r.color}`} style={{ width: `${Math.min((r.value / r.max) * 100, 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-2xs text-muted-foreground mb-2">Top Performing Segments</p>
            <div className="flex flex-wrap gap-2">
              {revenue.top_segments.map(s => (
                <span key={s} className="px-2 py-1 rounded-full bg-primary/10 text-primary text-2xs font-medium">{s}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-surface-2 p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="LinkIcon" size={16} className="text-primary" />
            Platform Integrations
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {integrations.map(i => (
              <div key={i.name} className="flex items-center gap-2 p-2 rounded-lg bg-surface-3">
                <Icon name={i.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-primary flex-shrink-0" />
                <span className="text-xs text-foreground truncate flex-1">{i.name}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Leads & Pipeline ────────────────────────────────────────────────────
function LeadsPipelineTab() {
  const [view, setView] = useState<'leads' | 'pipeline'>('leads');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={() => setView('leads')} className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${view === 'leads' ? 'bg-primary text-white' : 'bg-surface-3 text-muted-foreground hover:text-foreground'}`}>Leads</button>
        <button onClick={() => setView('pipeline')} className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${view === 'pipeline' ? 'bg-primary text-white' : 'bg-surface-3 text-muted-foreground hover:text-foreground'}`}>Pipeline</button>
      </div>

      {view === 'leads' && (
        <div className="rounded-xl border border-border bg-surface-2 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Active Leads</h3>
            <div className="flex items-center gap-2">
              <span className="text-2xs text-muted-foreground">AI-scored & ranked</span>
              <Icon name="SparklesIcon" size={14} className="text-primary" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  {['Contact', 'Source', 'Status', 'Destination', 'Budget', 'AI Score', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-2xs text-muted-foreground uppercase tracking-wide font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {MOCK_LEADS.map(lead => (
                  <tr key={lead.id} className="hover:bg-surface-3/50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-foreground">{lead.first_name} {lead.last_name}</p>
                        <p className="text-2xs text-muted-foreground">{lead.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 capitalize text-muted-foreground">{lead.source}</td>
                    <td className="px-4 py-3"><Badge label={lead.status} className={STATUS_COLORS[lead.status] || 'bg-slate-500/20 text-slate-300'} /></td>
                    <td className="px-4 py-3 text-muted-foreground">{lead.destination}</td>
                    <td className="px-4 py-3 font-medium text-foreground">{fmtCurrency(lead.budget)}</td>
                    <td className="px-4 py-3 w-32"><ScoreBar score={lead.score} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors" title="View"><Icon name="EyeIcon" size={14} /></button>
                        <button className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors" title="Call"><Icon name="PhoneIcon" size={14} /></button>
                        <button className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors" title="Email"><Icon name="EnvelopeIcon" size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === 'pipeline' && (
        <div className="space-y-3">
          <div className="rounded-xl border border-border bg-surface-2 overflow-hidden">
            <div className="px-5 py-3 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">Opportunities Pipeline</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    {['Opportunity', 'Stage', 'Value', 'Probability', 'Close Date', 'Destination'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-2xs text-muted-foreground uppercase tracking-wide font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {MOCK_OPPS.map(opp => (
                    <tr key={opp.id} className="hover:bg-surface-3/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground max-w-xs truncate">{opp.title}</td>
                      <td className="px-4 py-3"><Badge label={opp.stage.replace('_', ' ')} className={STAGE_COLORS[opp.stage] || 'bg-slate-500/20 text-slate-300'} /></td>
                      <td className="px-4 py-3 font-semibold text-foreground">{fmtCurrency(opp.value)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 rounded-full bg-surface-3">
                            <div className="h-1.5 rounded-full bg-primary" style={{ width: `${opp.probability}%` }} />
                          </div>
                          <span className="text-muted-foreground">{opp.probability}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{opp.expected_close_date}</td>
                      <td className="px-4 py-3 text-muted-foreground">{opp.destination}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Campaigns ───────────────────────────────────────────────────────────
function CampaignsTab() {
  const CHANNEL_ICONS: Record<string, string> = { email: 'EnvelopeIcon', sms: 'DevicePhoneMobileIcon', whatsapp: 'ChatBubbleLeftRightIcon', push: 'BellIcon', in_app: 'ComputerDesktopIcon' };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard label="Active Campaigns" value="8" sub="2 scheduled" icon="MegaphoneIcon" color="bg-purple-500/10 text-purple-400" />
        <KPICard label="Sent This Month" value="24,500" sub="across all channels" icon="PaperAirplaneIcon" color="bg-blue-500/10 text-blue-400" />
        <KPICard label="Avg Open Rate" value="28.4%" sub="+3.2% vs last month" icon="EnvelopeOpenIcon" color="bg-emerald-500/10 text-emerald-400" />
        <KPICard label="Revenue Attributed" value="$184K" sub="from campaigns" icon="BanknotesIcon" color="bg-amber-500/10 text-amber-400" />
      </div>

      <div className="rounded-xl border border-border bg-surface-2 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">Campaign Performance</h3>
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-medium hover:bg-primary/90 transition-colors">
            <Icon name="PlusIcon" size={14} />
            New Campaign
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                {['Campaign', 'Channel', 'Status', 'Audience', 'Sent', 'Opened', 'Clicked', 'Revenue'].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-2xs text-muted-foreground uppercase tracking-wide font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {MOCK_CAMPAIGNS.map(c => (
                <tr key={c.id} className="hover:bg-surface-3/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground">{c.name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Icon name={CHANNEL_ICONS[c.type] as Parameters<typeof Icon>[0]['name'] || 'EnvelopeIcon'} size={14} className="text-muted-foreground" />
                      <span className="capitalize text-muted-foreground">{c.type}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3"><Badge label={c.status} className={STATUS_COLORS[c.status] || 'bg-slate-500/20 text-slate-300'} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{fmt(c.audience_count)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmt(c.sent_count)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.sent_count > 0 ? `${Math.round((c.opened_count / c.sent_count) * 100)}%` : '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.sent_count > 0 ? `${Math.round((c.clicked_count / c.sent_count) * 100)}%` : '—'}</td>
                  <td className="px-4 py-3 font-medium text-foreground">{c.revenue_attributed > 0 ? fmtCurrency(c.revenue_attributed) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Automation Rules */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="BoltIcon" size={16} className="text-amber-400" />
          Follow-up Automations
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { name: 'New Lead Welcome', trigger: 'Lead Created', runs: 1847, active: true },
            { name: 'Quote Follow-up', trigger: 'Quote Sent', runs: 423, active: true },
            { name: 'Post-Booking Thank You', trigger: 'Booking Completed', runs: 2156, active: true },
            { name: 'Re-engagement Campaign', trigger: 'No Response 7d', runs: 892, active: true },
            { name: 'Birthday Greeting', trigger: 'Birthday', runs: 634, active: true },
          ].map(a => (
            <div key={a.name} className="flex items-center gap-3 p-3 rounded-lg bg-surface-3">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${a.active ? 'bg-emerald-400' : 'bg-slate-500'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground truncate">{a.name}</p>
                <p className="text-2xs text-muted-foreground">{a.trigger} · {fmt(a.runs)} runs</p>
              </div>
              <Icon name="BoltIcon" size={14} className="text-amber-400 flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Support & Tickets ───────────────────────────────────────────────────
function TicketsTab() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard label="Open Tickets" value="47" sub="12 urgent" icon="TicketIcon" color="bg-red-500/10 text-red-400" />
        <KPICard label="In Progress" value="23" sub="assigned to agents" icon="ArrowPathIcon" color="bg-blue-500/10 text-blue-400" />
        <KPICard label="Resolved Today" value="18" sub="avg 2.4h response" icon="CheckCircleIcon" color="bg-emerald-500/10 text-emerald-400" />
        <KPICard label="CSAT Score" value="4.7/5" sub="last 30 days" icon="StarIcon" color="bg-amber-500/10 text-amber-400" />
      </div>

      <div className="rounded-xl border border-border bg-surface-2 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">Support Tickets</h3>
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-medium hover:bg-primary/90 transition-colors">
            <Icon name="PlusIcon" size={14} />
            New Ticket
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                {['Ticket #', 'Subject', 'Category', 'Priority', 'Status', 'Channel', 'Created'].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-2xs text-muted-foreground uppercase tracking-wide font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {MOCK_TICKETS.map(t => (
                <tr key={t.id} className="hover:bg-surface-3/50 transition-colors">
                  <td className="px-4 py-3 font-mono text-primary text-2xs">{t.ticket_number}</td>
                  <td className="px-4 py-3 font-medium text-foreground max-w-xs truncate">{t.subject}</td>
                  <td className="px-4 py-3 capitalize text-muted-foreground">{t.category}</td>
                  <td className="px-4 py-3"><Badge label={t.priority} className={PRIORITY_COLORS[t.priority] || 'bg-slate-500/20 text-slate-300'} /></td>
                  <td className="px-4 py-3"><Badge label={t.status.replace('_', ' ')} className={STATUS_COLORS[t.status] || 'bg-slate-500/20 text-slate-300'} /></td>
                  <td className="px-4 py-3 capitalize text-muted-foreground">{t.channel}</td>
                  <td className="px-4 py-3 text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* SLA Status */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="ClockIcon" size={16} className="text-primary" />
          SLA Performance
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Critical (1h SLA)', met: 94, color: 'bg-red-500' },
            { label: 'Urgent (4h SLA)', met: 87, color: 'bg-orange-500' },
            { label: 'High (8h SLA)', met: 92, color: 'bg-amber-500' },
            { label: 'Medium (24h SLA)', met: 98, color: 'bg-emerald-500' },
          ].map(s => (
            <div key={s.label}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-muted-foreground">{s.label}</span>
                <span className="font-semibold text-foreground">{s.met}%</span>
              </div>
              <div className="h-2 rounded-full bg-surface-3">
                <div className={`h-2 rounded-full ${s.color}`} style={{ width: `${s.met}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Segments & Loyalty ──────────────────────────────────────────────────
function SegmentsLoyaltyTab() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Segments */}
        <div className="rounded-xl border border-border bg-surface-2 p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="UserGroupIcon" size={16} className="text-primary" />
            Customer Segments
          </h3>
          <div className="space-y-2">
            {MOCK_SEGMENTS.map(seg => (
              <div key={seg.id} className="flex items-center gap-3 p-3 rounded-lg bg-surface-3 hover:bg-surface-3/80 transition-colors">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${seg.is_active ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground">{seg.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {seg.tags.slice(0, 2).map(t => (
                      <span key={t} className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-2xs">{t}</span>
                    ))}
                    <span className="text-2xs text-muted-foreground capitalize">{seg.type}</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-foreground">{fmt(seg.contact_count)}</p>
                  <p className="text-2xs text-muted-foreground">contacts</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Loyalty Tiers */}
        <div className="rounded-xl border border-border bg-surface-2 p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="StarIcon" size={16} className="text-amber-400" />
            Loyalty Program
          </h3>
          <div className="space-y-3">
            {[
              { tier: 'Diamond', count: 23, points_avg: 84200, color: 'text-cyan-400', bg: 'bg-cyan-500/10', bar: 'bg-cyan-500' },
              { tier: 'Platinum', count: 119, points_avg: 42800, color: 'text-purple-400', bg: 'bg-purple-500/10', bar: 'bg-purple-500' },
              { tier: 'Gold', count: 387, points_avg: 18400, color: 'text-amber-400', bg: 'bg-amber-500/10', bar: 'bg-amber-500' },
              { tier: 'Silver', count: 1204, points_avg: 7200, color: 'text-slate-300', bg: 'bg-slate-500/10', bar: 'bg-slate-400' },
              { tier: 'Standard', count: 6687, points_avg: 2100, color: 'text-muted-foreground', bg: 'bg-surface-3', bar: 'bg-surface-3' },
            ].map(l => (
              <div key={l.tier} className={`flex items-center gap-3 p-3 rounded-lg ${l.bg}`}>
                <Icon name="StarIcon" size={16} className={l.color} />
                <div className="flex-1">
                  <div className="flex justify-between text-xs mb-1">
                    <span className={`font-semibold ${l.color}`}>{l.tier}</span>
                    <span className="text-muted-foreground">{fmt(l.count)} members</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-surface-3">
                    <div className={`h-1.5 rounded-full ${l.bar}`} style={{ width: `${Math.min((l.count / 6687) * 100, 100)}%` }} />
                  </div>
                </div>
                <span className="text-xs font-medium text-foreground flex-shrink-0">{fmt(l.points_avg)} pts</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-lg font-bold text-foreground">8,420</p>
              <p className="text-2xs text-muted-foreground">Total Members</p>
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">2.4M</p>
              <p className="text-2xs text-muted-foreground">Points Issued</p>
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">$184K</p>
              <p className="text-2xs text-muted-foreground">Points Redeemed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Corporate Accounts */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="BuildingOffice2Icon" size={16} className="text-primary" />
          Corporate & B2B Accounts
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { name: 'Apex Global Enterprises', type: 'Corporate', country: 'USA', bookings: 47, ltv: 284500, tier: 'Platinum' },
            { name: 'Meridian Travel Agency', type: 'Agency', country: 'UK', bookings: 312, ltv: 1850000, tier: 'Gold' },
            { name: 'Summit Consulting Group', type: 'Corporate', country: 'Australia', bookings: 23, ltv: 142000, tier: 'Silver' },
            { name: 'Pacific Rim Adventures', type: 'Agency', country: 'Singapore', bookings: 189, ltv: 920000, tier: 'Gold' },
            { name: 'Nordic Business Travel', type: 'B2B', country: 'Norway', bookings: 78, ltv: 456000, tier: 'Silver' },
          ].map(co => (
            <div key={co.name} className="p-4 rounded-lg bg-surface-3 border border-border">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-xs font-semibold text-foreground">{co.name}</p>
                  <p className="text-2xs text-muted-foreground">{co.type} · {co.country}</p>
                </div>
                <Badge label={co.tier} className={co.tier === 'Platinum' ? 'bg-purple-500/20 text-purple-300' : co.tier === 'Gold' ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-500/20 text-slate-300'} />
              </div>
              <div className="flex justify-between text-xs mt-3">
                <div>
                  <p className="text-muted-foreground">Bookings</p>
                  <p className="font-semibold text-foreground">{co.bookings}</p>
                </div>
                <div className="text-right">
                  <p className="text-muted-foreground">Lifetime Value</p>
                  <p className="font-semibold text-foreground">{fmtCurrency(co.ltv)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: AI Copilot ──────────────────────────────────────────────────────────
function AICopilotTab() {
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([
    { role: 'assistant', content: 'Hello! I\'m your CRM AI Copilot. I can help you with lead scoring, next-best-action recommendations, revenue forecasting, customer insights, and sales strategy. What would you like to know?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const res = await fetch('/api/ai/chat-completion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [
            { role: 'system', content: 'You are an expert CRM AI Copilot for WorldwayLuxe, a luxury travel company. You help sales agents with lead scoring, customer insights, next-best-action recommendations, revenue forecasting, campaign strategy, and customer support. Be concise, data-driven, and actionable.' },
            ...messages,
            { role: 'user', content: userMsg }
          ]
        })
      });
      const data = await res.json();
      const reply = data.content || data.message || data.choices?.[0]?.message?.content || 'I\'m here to help with your CRM needs. Could you provide more details?';
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'I\'m processing your request. For immediate assistance, please check the lead scoring dashboard or contact your account manager.' }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages]);

  return (
    <div className="space-y-4">
      {/* AI Suggestions */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="SparklesIcon" size={16} className="text-primary" />
          AI Next-Best-Action Recommendations
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {COPILOT_SUGGESTIONS.map((s, i) => (
            <div key={i} className="p-4 rounded-lg bg-surface-3 border border-border hover:border-primary/30 transition-colors">
              <div className="flex items-start gap-3">
                <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={18} className={s.color} />
                <div>
                  <p className="text-xs font-semibold text-foreground mb-1">{s.title}</p>
                  <p className="text-2xs text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              </div>
              <button className="mt-3 w-full py-1.5 rounded-lg bg-primary/10 text-primary text-2xs font-medium hover:bg-primary/20 transition-colors">
                Take Action
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat Interface */}
      <div className="rounded-xl border border-border bg-surface-2 flex flex-col" style={{ height: '420px' }}>
        <div className="flex items-center gap-2 px-5 py-3 border-b border-border flex-shrink-0">
          <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
            <Icon name="SparklesIcon" size={14} className="text-primary" />
          </div>
          <div>
            <p className="text-xs font-semibold text-foreground">CRM AI Copilot</p>
            <p className="text-2xs text-emerald-400">Online · Powered by Gemini</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-xs leading-relaxed ${m.role === 'user' ? 'bg-primary text-white' : 'bg-surface-3 text-foreground'}`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-surface-3 rounded-xl px-4 py-2.5 flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 p-3 border-t border-border flex-shrink-0">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder="Ask about leads, pipeline, campaigns, forecasts..."
            className="flex-1 bg-surface-3 border border-border rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
          />
          <button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            className="p-2 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Icon name="PaperAirplaneIcon" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EnterpriseCRMContent() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [overview, setOverview] = useState<CRMOverview | null>(null);

  useEffect(() => {
    fetch('/api/crm?action=overview')
      .then(r => r.json())
      .then(setOverview)
      .catch(() => {
        // Fallback mock data
        setOverview({
          contacts: { total: 8420, new_this_month: 312, vip: 142, corporate: 89 },
          leads: { total: 1847, new_today: 23, qualified: 456, converted: 187, conversion_rate: 10.1 },
          pipeline: { total_value: 4280000, opportunities: 234, avg_deal_size: 18290, win_rate: 34.2 },
          tickets: { open: 47, in_progress: 23, resolved_today: 18, avg_response_hours: 2.4 },
          campaigns: { active: 8, sent_this_month: 24500, avg_open_rate: 28.4, revenue_attributed: 184000 },
          revenue: { forecast_30d: 1240000, forecast_90d: 3820000, clv_avg: 12400, top_segments: ['VIP Travelers', 'Corporate Accounts', 'Luxury Cruise Enthusiasts'] }
        });
      });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-surface-2 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Icon name="UsersIcon" size={22} className="text-primary" />
              Enterprise CRM & Sales Automation
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">Customer 360° · Lead Management · AI-Powered Sales Intelligence</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 text-2xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Production Live
            </span>
            <a href="/crm-report" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors">
              <Icon name="DocumentChartBarIcon" size={14} />
              CRM Report
            </a>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mt-4 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground hover:bg-surface-3'
              }`}
            >
              <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'overview' && <OverviewTab overview={overview} />}
        {activeTab === 'leads' && <LeadsPipelineTab />}
        {activeTab === 'campaigns' && <CampaignsTab />}
        {activeTab === 'tickets' && <TicketsTab />}
        {activeTab === 'segments' && <SegmentsLoyaltyTab />}
        {activeTab === 'copilot' && <AICopilotTab />}
      </div>
    </div>
  );
}
